/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import { loginRequest, googleLoginRequest, logoutRequest, type LoginPayload, type User } from "../api/authService";
import { isTokenExpired } from "../jwt";
import { setCsrfToken, clearCsrfToken, setRefreshToken, clearRefreshToken } from "../csrfToken";
import { refreshAccessToken } from "@/api/privateClient";

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  /** True while an expired-token session is still being silently refreshed on app load — routes show a loader instead of bouncing to /login. */
  restoring: boolean;
  error: string | null;
}

function readStoredSession(): { user: User | null; token: string | null } {
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "null");

  // A token left over from a previous session that has already expired
  // (or was tampered with — decodeJwt returns null for a malformed token)
  // must never be treated as a live session. Without this check, the SPA
  // would render protected routes (e.g. /super-admin/*) with a stale
  // `user` object before the first API call 401s.
  //
  // Note: localStorage itself is deliberately left untouched here (unlike
  // the old version of this function) — the access token expiring after
  // 15m does NOT mean the session is over, only that this specific token
  // is stale. restoreSession() below reads the raw values straight back
  // out of localStorage to attempt a silent refresh using the still-valid
  // refresh-token cookie. Wiping them here would destroy that chance on
  // every plain page reload/revisit, forcing a full re-login far more
  // often than the refresh-token's actual lifetime warrants.
  if (isTokenExpired(token)) {
    return { user: null, token: null };
  }

  return { user, token };
}

const storedSession = readStoredSession();

const initialState: AuthState = {
  ...storedSession,
  loading: false,
  restoring: !storedSession.token && !!localStorage.getItem("token") && !!localStorage.getItem("user"),
  error: null,
};

export const loginUser = createAsyncThunk(
  "auth/login",
  async (payload: LoginPayload, { rejectWithValue }) => {
    try {
      return await loginRequest(payload);
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || "Login failed. Check your credentials.");
    }
  }
);
export const loginWithGoogle = createAsyncThunk(
  "auth/loginWithGoogle",
  async (idToken: string, { rejectWithValue }) => {
    try {
      return await googleLoginRequest(idToken);
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || "Google sign-in failed.");
    }
  }
);

/**
 * Attempts to silently restore a session whose access token has expired
 * (or was never loaded into this tab) but whose refresh-token cookie may
 * still be good — e.g. revisiting /login (or any page) more than 15
 * minutes after the last request, well within the refresh token's own
 * lifetime. Reads the raw localStorage values directly rather than
 * state.auth, since readStoredSession() already nulled those out of the
 * initial state for anything expired. Only clears the stored session on
 * an actual refresh failure (refresh token itself expired/revoked/absent).
 */
export const restoreSession = createAsyncThunk(
  "auth/restoreSession",
  async (_: void, { rejectWithValue }) => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "null");
    const hadStoredSession = !!localStorage.getItem("token") && !!storedUser;
    if (!hadStoredSession) return rejectWithValue(null);

    try {
      const token = await refreshAccessToken();
      return { user: storedUser as User, token };
    } catch {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      clearCsrfToken();
      clearRefreshToken();
      return rejectWithValue(null);
    }
  }
);

export const logoutUser = createAsyncThunk("auth/logout", async () => {
  try {
    await logoutRequest();
  } catch {
    // Even if the API call fails (network issue, already-expired token, etc.),
    // we still want to clear local session — don't block logout on this.
  }
  return true;
})
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ user: User; token: string; refreshToken?: string; csrfToken?: string }>
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      localStorage.setItem("token", action.payload.token);
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      if (action.payload.csrfToken) setCsrfToken(action.payload.csrfToken);
      if (action.payload.refreshToken) setRefreshToken(action.payload.refreshToken);
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      clearCsrfToken();
      clearRefreshToken();
    },
    // Keeps Redux's token in sync after a silent refresh (see
    // auth/sessionSync.ts) — localStorage is updated by privateClient.ts
    // itself; this just mirrors that into state so ProtectedRoute/
    // PublicRoute's isTokenExpired checks see the renewed token instead
    // of the one issued at login.
    tokenRefreshed: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    // The modules and features saved at login go stale when an admin switches a module on or off, so they are re-read from the
    // server (see auth/AccessSync.tsx). Only a real change touches state, so nothing re-renders for an unchanged answer.
    accessRefreshed: (state, action: PayloadAction<{ modules: string[] | null; features: string[]; permissions?: Record<string, string[]> }>) => {
      if (!state.user) return;
      const next = { modules: action.payload.modules, features: action.payload.features, permissions: action.payload.permissions ?? {} };
      if (JSON.stringify([state.user.modules ?? null, state.user.features, state.user.permissions ?? {}]) === JSON.stringify([next.modules, next.features, next.permissions])) return;
      state.user.modules = next.modules;
      state.user.features = next.features;
      state.user.permissions = next.permissions;
      localStorage.setItem("user", JSON.stringify(state.user));
    },
  },
  extraReducers: (builder) => {
  builder
    .addCase(loginUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(loginUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      localStorage.setItem("token", action.payload.token);
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      setCsrfToken(action.payload.csrfToken);
      setRefreshToken(action.payload.refreshToken);
    })
    .addCase(loginUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    })
    .addCase(loginWithGoogle.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(loginWithGoogle.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      localStorage.setItem("token", action.payload.token);
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      setCsrfToken(action.payload.csrfToken);
      setRefreshToken(action.payload.refreshToken);
    })
    .addCase(loginWithGoogle.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    })
    .addCase(logoutUser.fulfilled, (state) => {
      state.user = null;
      state.token = null;
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      clearCsrfToken();
      clearRefreshToken();
    })
    .addCase(restoreSession.pending, (state) => {
      state.restoring = true;
    })
    .addCase(restoreSession.rejected, (state) => {
      state.restoring = false;
    })
    .addCase(restoreSession.fulfilled, (state, action) => {
      state.restoring = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      localStorage.setItem("user", JSON.stringify(action.payload.user));
      // token itself is already persisted to localStorage by refreshAccessToken().
    });
},
});

export const { setCredentials, logout, tokenRefreshed, accessRefreshed } = authSlice.actions;
export default authSlice.reducer;